import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import type { Locale } from "date-fns";
import { format } from "date-fns";
import {
  LEAVE_CALENDAR_CELL_WIDTH,
  LEAVE_CALENDAR_HEADER_HEIGHT,
} from "./leaveCalendar.constants";
import {
  dayKey,
  formatHeaderDay,
  isToday,
} from "./leaveCalendarDates";
import type { PublicHoliday } from "./leaveCalendar.types";

export interface LeaveCalendarDateHeaderProps {
  days: Date[];
  locale: Locale;
  holidays: PublicHoliday[];
  holidayName: (h: PublicHoliday) => string;
  saturdayOff?: boolean;
  sundayOff?: boolean;
  /** Reserve space on the right so day columns line up with the grid (vertical list scrollbar). */
  rightGutterPx?: number;
  cellWidth?: number;
  /** Horizontal scroller — keeps month labels pinned while their month is visible. */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  /** A holiday column was tapped; a touch screen has no tooltip to show the holiday's name. */
  onHolidayTap?: (date: Date, name: string) => void;
}

export function LeaveCalendarDateHeader({
  days,
  locale,
  holidays,
  holidayName,
  saturdayOff = true,
  sundayOff = true,
  rightGutterPx = 0,
  cellWidth = LEAVE_CALENDAR_CELL_WIDTH,
  scrollContainerRef,
  onHolidayTap,
}: LeaveCalendarDateHeaderProps) {
  const w = cellWidth;
  const h = LEAVE_CALENDAR_HEADER_HEIGHT;
  const topH = 24;
  const botH = h - topH;

  const monthGroups = useMemo(() => {
    const list: { key: string; name: string; count: number; startPx: number }[] =
      [];
    let lastKey = "";
    let startPx = 0;
    for (const d of days) {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (key !== lastKey) {
        list.push({
          key,
          name: format(d, "LLLL yyyy", { locale }),
          count: 1,
          startPx,
        });
        lastKey = key;
      } else {
        list[list.length - 1].count++;
      }
      startPx += w;
    }
    return list;
  }, [days, locale, w]);

  const [scrollLeft, setScrollLeft] = useState(0);
  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;
    let raf = 0;
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrollLeft(el.scrollLeft));
    };
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", sync);
    };
  }, [scrollContainerRef, days.length, w]);

  return (
    <div
      className="flex shrink-0 flex-col border-b border-slate-200 bg-white/90 shadow-sm"
      style={{
        height: h,
        width: days.length * w + rightGutterPx,
        display: "grid",
        gridTemplateRows: `${topH}px ${botH}px`,
      }}
    >
      <div
        className="flex shrink-0 border-b border-slate-100 bg-slate-50"
        style={{ height: topH }}
      >
        {monthGroups.map((m) => {
          const groupWidth = m.count * w;
          const labelReserve = Math.min(groupWidth, 112);
          const maxTravel = Math.max(0, groupWidth - labelReserve);
          const labelOffset = Math.min(
            Math.max(0, scrollLeft - m.startPx),
            maxTravel
          );

          return (
            <div
              key={m.key}
              className="relative shrink-0 overflow-hidden border-r border-slate-100"
              style={{ width: groupWidth, height: topH }}
            >
              <span
                className="absolute top-0 left-0 inline-flex h-full items-center whitespace-nowrap bg-slate-50 px-3 text-[11px] font-extrabold uppercase tracking-widest text-slate-500 lg:text-[10px]"
                style={{ transform: `translateX(${labelOffset}px)` }}
                title={m.name}
              >
                {m.name}
              </span>
            </div>
          );
        })}
        {rightGutterPx > 0 ? (
          <div
            className="shrink-0 border-l border-slate-100 bg-slate-50"
            style={{ width: rightGutterPx }}
            aria-hidden
          />
        ) : null}
      </div>

      <div
        className="flex flex-1 items-stretch"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${days.length}, ${w}px) ${rightGutterPx}px`,
        }}
      >
        {days.map((d) => {
          const key = dayKey(d);
          const day = d.getDay();
          const weekend =
            (day === 6 && saturdayOff) || (day === 0 && sundayOff);
          const hol = holidays.find((x) => x.date === key);
          const today = isToday(d);
          const { dow, dom } = formatHeaderDay(d, locale);

          return (
            <div
              key={key}
              className={clsx(
                "flex flex-col items-center justify-center border-r border-slate-100/70 text-center relative transition-all duration-200",
                today && hol
                  ? "z-[1]"
                  : today
                  ? "bg-teal-50/30 z-[1] ring-1 ring-inset ring-teal-500/20"
                  : hol
                  ? "bg-amber-50/40"
                  : weekend
                  ? "bg-slate-50/50"
                  : ""
              )}
              style={{
                width: w,
                minWidth: w,
                height: botH,
                boxSizing: "border-box",
                // When today + holiday: split diagonal background so both are visible
                ...(today && hol
                  ? {
                      background:
                        "linear-gradient(135deg, #f0fdfa 50%, #fffbeb 50%)",
                      boxShadow: "inset 0 0 0 1px rgba(20,184,166,0.25)",
                    }
                  : {}),
              }}
              title={hol ? holidayName(hol) : `${dow} ${dom}`}
              onClick={hol && onHolidayTap ? () => onHolidayTap(d, holidayName(hol)) : undefined}
            >
              {/* Day-of-week label */}
              <span
                className={clsx(
                  "text-[11px] font-bold uppercase leading-none tracking-wider lg:text-[9px]",
                  today
                    ? "text-teal-600"
                    : hol
                    ? "text-amber-600"
                    : weekend
                    ? "text-slate-400/90"
                    : "text-slate-400"
                )}
              >
                {dow}
              </span>

              {/* Date number — today circle; holiday badge on corner when both */}
              <span
                className={clsx(
                  "relative mt-0.5 flex items-center justify-center",
                  today && "h-6 w-6"
                )}
              >
                <span
                  className={clsx(
                    "text-[12px] font-extrabold leading-none flex items-center justify-center transition-all duration-200 hover:scale-110",
                    today
                      ? "h-6 w-6 rounded-full bg-teal-600 text-white shadow-md shadow-teal-600/40 ring-2 ring-teal-100"
                      : hol
                      ? "text-amber-700 font-bold"
                      : weekend
                      ? "text-slate-500 font-bold"
                      : "text-slate-700"
                  )}
                >
                  {dom}
                </span>
                {today && hol ? (
                  <span
                    className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-amber-500 shadow-sm ring-1 ring-white"
                    aria-hidden
                  />
                ) : null}
              </span>

              {/* Holiday dot — non-today holidays only (today uses corner badge) */}
              {hol && !today ? (
                <span className="absolute bottom-1 inline-flex items-center">
                  <span className="inline-block h-1 w-1 rounded-full bg-amber-500 shadow-sm" />
                </span>
              ) : null}
            </div>
          );
        })}
        {rightGutterPx > 0 ? (
          <div
            className="shrink-0 bg-white"
            style={{ width: rightGutterPx }}
            aria-hidden
          />
        ) : null}
      </div>
    </div>
  );
}
